import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// ── Types (mirror the render-optimized view returned by the API) ────────────────

/**
 * Coarse enrichment state the drawer renders against. Mirrors the backend
 * `EnrichmentState` in services/enrichment/api/enrichment-view.ts.
 */
export type EnrichmentState =
  | 'pending'       // enqueued / collecting — no diagnosis yet
  | 'applied'       // concrete diagnosis produced and posted to the ticket
  | 'no_evidence'   // no correlated logs were found
  | 'inconclusive'  // logs found but matched no known failure pattern
  | 'failed'        // enrichment errored out
  | 'not_found';    // no enrichment record exists for this finding

export interface DiagnosisProse {
  summary?:  string;
  [k: string]: unknown;
}

/** Flattened, render-ready diagnosis snapshot persisted on the ENRICH# record. */
export interface DiagnosisSnapshot {
  version:           number;
  category:          string;
  confidence:        'HIGH' | 'MEDIUM' | 'LOW';
  confidenceScore:   number;
  observedSymptom:   string;
  likelyCause:       string;
  likelyError:       string | null;
  suggestedNextSteps: string[];
  topSignature:      { template: string; count: number; sample: string } | null;
  evidenceRef:       string;
  hasEvidence:       boolean;
  totalErrorLines:   number;
  prose?:            DiagnosisProse | null;
  proseMeta?:        { mode: string; source: string; failureReason?: string } | null;
  diagnosedAt:       string;
}

export interface EnrichmentView {
  findingId:       string;
  state:           EnrichmentState;
  status:          string | null;
  diagnosis:       DiagnosisSnapshot | null;
  createdAt?:      string;
  updatedAt?:      string;
  ticketUpdatedAt?: string;
}

/** One ranked, redacted error signature from the persisted evidence bundle. */
export interface EvidenceSignature {
  template:    string;
  count:       number;
  sample:      string;
  firstSeenMs: number;
  lastSeenMs:  number;
}

/** UI-facing projection of a persisted evidence bundle (provenance + summary). */
export interface EvidenceSummary {
  findingId:       string;
  version:         number;
  provider:        string;
  serviceType:     string;
  logSource:       string | null;
  collectedAt:     string;
  storedAt:        string;
  window:          { startMs: number; endMs: number };
  logAvailable:    boolean;
  logReason?:      string;
  totalErrorLines: number;
  topSignatures:   EvidenceSignature[];
  metrics:         Record<string, number>;
  queries:         { kind: string; target: string; status: string; latencyMs: number; truncated: boolean }[];
  redactionApplied: boolean;
  sizeBytes:       number;
}

export interface EnrichmentResponse {
  enrichment: EnrichmentView;
  /** Present only when requested via `getEnrichment(id, { includeEvidence: true })`. */
  evidence?: EvidenceSummary | null;
}

// ── Service ─────────────────────────────────────────────────────────────────────

/**
 * Read-only access to a finding's enrichment/diagnosis. The endpoint always
 * returns HTTP 200 with an `enrichment` view (state `not_found` when there is
 * none), so callers never have to error-handle a missing record — but network
 * failures should still be caught so the drawer never blocks on this fetch.
 */
@Injectable({ providedIn: 'root' })
export class EnrichmentService {
  private readonly base = `${environment.apiUrl}/api/v1/monitoring`;

  constructor(private readonly http: HttpClient) {}

  getEnrichment(findingId: string, opts?: { includeEvidence?: boolean }): Observable<EnrichmentResponse> {
    const query = opts?.includeEvidence ? '?include=evidence' : '';
    return this.http.get<EnrichmentResponse>(
      `${this.base}/findings/${encodeURIComponent(findingId)}/enrichment${query}`,
    );
  }
}
